import React, { useState, useEffect } from "react";
import PulseLoader from "react-spinners/PulseLoader";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { formatAiMessage } from "@/utils/formatAiMessage";
import { TRINITY_INSIGHT_PROMPTS } from "@/data/trinity/trinityInsightPrompts"; // 04 Jun 2025

import { useSaveInsightChatForContactNoteMutation } from "@/features/userConversations/userConversationsApiSlice"; // 06 Jun 2025

// import { toast } from 'react-toastify'; // 06 Jun 2025

const EtTrinityConversationInsightsServiceBar = ({
  userId ="",
  conversationId="",
  noteId="",
  contactName="",
  generatedInsightHtml="",
  insightChatHistory = [], // ✅ NEW PROP - 06 Jun 2025
  onRefetchChatHistory = null, // ✅ NEW - 07 Jun 2025
  onClose
}) => {

  // First Created - 03 Jun 2025
  // Author - Atul Pandey
  // Purpose - To enable focused AI exploration based on weekly user conversations (Trinity Weekly Insights)
  // Updated - 04 Jun 2025 - Refactored based on revised insights logic
  // Updated - 06 Jun 2025 - Added mutation to save insights chat history
  // Updated - 07 Jun 2025 - Added prompt filtering based on insightChatHitsory
  // Updated - 07 Jun 2025 - Added refetch
  // Updated - 30 Jul 2025 - Moved generateInsightIfMissing to EtTrinityWeeklyInsights

  // console.log('insightChatHistory', insightChatHistory)

  // 1a - State Variables
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localInsight, setLocalInsight] = useState(generatedInsightHtml || "") // 04 Jun 2025
  const [saveInsightChat] = useSaveInsightChatForContactNoteMutation(); // 06 Jun 2025 ✅ NEW
  const [isSaving, setIsSaving] = useState(true); // 06 Jun 2025

  // 1b – Derive answered prompts - 07 Jun 2025
  const answeredPromptLabels = (insightChatHistory || [])
    ?.filter(msg => msg.role === 'user')
    ?.map(msg => msg.content?.trim());

  // 1c – Filter prompts - 07 Jun 2025
  const unansweredPrompts = TRINITY_INSIGHT_PROMPTS?.filter(
    prompt => !answeredPromptLabels.includes(prompt.label.trim())
  );

  // 2 - Stream Handler Function - 04 Jun 2025
  const handleSendWithContext = async (userText) => {
    const systemContext = `
        You are Trinity, a helpful assistant providing actionable insights from weekly client conversations.

        Context:
        - Contact Name: ${contactName}
        - Insight Summary: ${localInsight.replace(/(<([^>]+)>)/gi, "").slice(0, 1200)}

        Respond clearly in HTML using TailwindCSS:
        - Use <p class='text-xs leading-relaxed'> for paragraphs
        - Use <ul class='list-disc pl-5 text-xs'> or <ol class='list-decimal pl-5 text-xs'> for bullets
        - Keep it short and useful
        `;

    const userMessage = { role: "user", content: userText };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
        const eventSource = new EventSource(
        `${process.env.REACT_APP_SERVER_URL}/trinity/generateResponse?` +
        `messages=${encodeURIComponent(JSON.stringify([
            { role: "system", content: systemContext },
            userMessage
        ]))}` +
        `&maxTokens=1000&model=gpt-4`
        );

        let aiResponse = "";

        eventSource.onmessage = (event) => {
        const chunk = event.data;
        aiResponse += chunk;

        setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant') {
            return [...prev.slice(0, -1), { ...last, content: aiResponse }];
            }
            return [...prev, { role: "assistant", content: chunk }];
        });
        };

        eventSource.addEventListener("end", async () => {
          eventSource.close();
          setLoading(false);

          const newUserMessage = { role: "user", content: userText };
          const newAssistantMessage = { role: "assistant", content: aiResponse };

          // 07 Jun 2025
          const finalChat = [
            ...messages,
            newUserMessage,
            newAssistantMessage
          ];

          // Set the updated messages in state
          setMessages(finalChat);
        
          // 06 Jun 2025 - Save chat history in DB
          try {
            await saveInsightChat({
              userId,
              userConversationId: conversationId,
              contactNoteId: noteId,
              // chatHistory: messages,
              chatHistory: finalChat, // 07 Jun 2025
              generatedInsightHtml: localInsight // optional but helps keep in sync
            });
            console.log("✅ Chat history saved");
            // ✅ Refetch the updated chat history
              if (onRefetchChatHistory) {
                onRefetchChatHistory();
              }
            // ✅ Clear chat state locally to avoid duplicate sends
            setMessages([]);
          } catch (err) {
            console.error("❌ Failed to save chat history:", err);
          }
        });

        eventSource.onerror = (error) => {
          console.error("Stream error:", error);
          eventSource.close();
          setLoading(false);
        };

    } catch (error) {
        console.error("Streaming failure:", error);
        setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong." }
        ]);
        setLoading(false);
    }
  };

  // 3 - handlePromptClick - 04 Jun 2025
  const handlePromptClick = (promptText) => {
    const fullPrompt = `${promptText}`;
    handleSendWithContext(fullPrompt);
  };

  // 4 - Generate Insights if Missing - 04 Jun 2025, 30 Jul 2025 - Moved to EtTrinityWeeklyInsights
  // const generateInsightIfMissing = async () => {  
  //   if (!localInsight && conversationId && noteId) {
  //     try {
  //       setLoading(true);
  //       const eventSource = new EventSource(
  //         `${process.env.REACT_APP_SERVER_URL}/userConversations/${conversationId}/notes/${noteId}/generateInsight`
  //       );

  //       let streamedHtml = "";

  //       eventSource.onmessage = (event) => {
  //         const chunk = event.data;
  //         streamedHtml += chunk;
  //         setLocalInsight((prev) => prev + chunk); // progressive update
  //       };

  //       eventSource.addEventListener("end", () => {
  //         eventSource.close();
  //         setLoading(false);
  //       });

  //       eventSource.onerror = (err) => {
  //         console.error("Stream error while generating insight:", err);
  //         eventSource.close();
  //         setLoading(false);
  //       };
  //     } catch (err) {
  //       console.error("Insight generation failed:", err);
  //       setLoading(false);
  //     }
  //   }
  // };

// 4b - UseEffect - Run if missing - 04 Jun 2025 30 Jul 2025 - Removed 
// useEffect(() => {
//   generateInsightIfMissing();
// }, []);


  
  // 7 - Render UI
  return (
    <div>
      {unansweredPrompts?.length >0 && (
      <div className="mt-4 bg-white shadow-lg shadow-black rounded-lg w-full p-4 font-poppins border border-gray-300">
        {/* Header */}
        {/* <div className="flex justify-between items-center pb-2">
          {/* <div>
            <p className="text-sm font-bold text-gray-800">🧠 Trinity Insight Companion</p>
            <p className="text-[10px] text-gray-500">
              Explore follow-up insights based on your conversation with <span className="font-semibold">{contactName}</span>.
              </p>
          </div> */}
          {/* <button
            onClick={onClose}
            className="text-[10px] text-orange-600 hover:underline"
          >
            Close
          </button> 
        </div> */}

        {/* 04 Jun 2025 - Prompt Options or Fallback Message */}
      <div className="my-3">

      {loading && !localInsight && ( 
          
          <div className="flex items-center space-x-2 text-xs text-gray-500 italic mb-2">
              <span>Generating insight...</span>
              <PulseLoader color="#fff" size={6} />
          </div>
      )}

      {localInsight ? (
          <div>
            {/* 04 Jun 2025 - If no messages */}
            {messages?.length === 0 && (
              <div className=" flex justify-between items-center p-4">
                  <p className="text-orange-600 text-lg text-center">Select a prompt to build a follow-up</p>
                  <button
                    onClick={onClose}
                    className="text-[10px] text-orange-600 hover:underline"
                  >
                    Close
                  </button>
              </div>
              )}
            <div className="grid grid-cols-1 border-t">
              {/* {TRINITY_INSIGHT_PROMPTS?.map((prompt) => ( */}
              {unansweredPrompts?.map((prompt) => (
                  <div
                  key={prompt.id}
                  onClick={() => handlePromptClick(prompt.label)}
                  disabled={loading}
                  className="border-b px-3 py-2 hover:bg-orange-50 transition disabled:opacity-50 cursor-pointer"
                  >
                    <p className="text-lg">{prompt.label}</p>
                  </div>
              ))}
            </div>
          </div>
          ) : (
            <div className="flex items-center justify-between text-[11px] text-gray-500 italic">
                <p>Please generate an insight first to explore follow-up suggestions.</p>
                {/* <button
                onClick={generateInsightIfMissing}
                disabled={loading}
                className="text-[10px] text-orange-600 ml-4 border border-orange-400 px-2 py-1 rounded hover:bg-orange-50 disabled:opacity-50"
                >
                + Generate Trinity Insight
                </button> */}
            </div>
          )}
      </div>

        


        {/* Chat Message Display */}
        <div className="overflow-y-auto max-h-[40vh] p-2 space-y-3">
          {messages?.map((msg, i) => (
            <div
              key={i}
              className={`p-2 rounded text-xs ${
                msg.role === "user" ? "bg-orange-100 text-right" : "bg-gray-100 text-left"
              }`}
            >
              
                <div dangerouslySetInnerHTML={{ __html: formatAiMessage(msg.content) }} />
            
            </div>
          ))}
        </div>

        {/* 06 Jun 2025 - Save Button */}
        {/* {messages.length > 0 && (
          <div className="flex justify-end mt-2"> 
            <button
              onClick={async () => {
                setIsSaving(true);
                try {
                  await saveInsightChat({
                    userId,
                    userConversationId: conversationId,
                    contactNoteId: noteId,
                    chatHistory: messages,
                    generatedInsightHtml: localInsight
                  });
                  alert("✅ Chat saved successfully!");
                } catch (err) {
                  console.error("❌ Failed to save chat history:", err);
                  alert("❌ Save failed. Try again.");
                } finally {
                  setIsSaving(false);
                }
              }}
              className={`text-xs px-3 py-1 rounded border ${
                isSaving
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
              }`}
            >
              {isSaving ? "Saving..." : "💾 Save Chat"}
            </button>
          </div>
        )} */}
      </div>
      )}
    </div>
  );
};

export default EtTrinityConversationInsightsServiceBar;
